import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Cookie, Database, Mail, UserX } from "lucide-react";
import Logo from "@/components/Logo";

/**
 * Politique de confidentialité (RGPD / LPRPDE) — accessible sans login.
 */
export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#F5F5F0] p-4 md:p-8" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 flex items-center justify-between gap-3">
          <Logo size={36} />
          <Link
            to="/login"
            className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-black inline-flex items-center gap-1"
          >
            <ArrowLeft size={12} strokeWidth={2.4} /> Retour à la connexion
          </Link>
        </div>

        <div className="card-flat">
          <div className="label mb-1">Confidentialité</div>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight mb-2">
            Politique de confidentialité.
          </h1>
          <p className="text-sm text-[var(--muted)] mb-8 leading-relaxed">
            Dernière mise à jour : 27 février 2026
          </p>

          <Section icon={<Shield size={16} strokeWidth={2.4} />} title="Qui est responsable">
            <p>
              Gestion Dek est exploitée par <strong>Les Samouraï du Web inc.</strong>
              {" "}(Québec, Canada). Pour toute question concernant tes données :
            </p>
            <ul className="mt-2 space-y-1 list-disc list-inside">
              <li>Courriel : <a href="mailto:info@lesamouraiduweb.com" className="underline">info@lesamouraiduweb.com</a></li>
            </ul>
          </Section>

          <Section icon={<Database size={16} strokeWidth={2.4} />} title="Données que nous collectons">
            <p>
              Nous collectons strictement les informations nécessaires à la gestion
              de tes activités d&apos;arbitrage et de marquage :
            </p>
            <ul className="mt-2 space-y-1 list-disc list-inside">
              <li><strong>Identité</strong> : prénom, nom, courriel, numéro de téléphone</li>
              <li><strong>Authentification</strong> : mot de passe (hashé avec bcrypt, jamais en clair)</li>
              <li><strong>Activité d&apos;arbitrage</strong> : disponibilités, assignations, confirmations, paies</li>
              <li><strong>Journaux techniques</strong> : adresse IP au moment de la connexion, type de navigateur (à des fins de sécurité)</li>
            </ul>
            <p className="mt-2">
              Nous ne collectons <strong>jamais</strong> : géolocalisation précise,
              données biométriques, données de carte de crédit (paiements gérés par
              des tiers conformes PCI-DSS), historique de navigation hors plateforme.
            </p>
          </Section>

          <Section icon={<Cookie size={16} strokeWidth={2.4} />} title="Cookies">
            <p>
              Gestion Dek utilise uniquement des <strong>cookies strictement nécessaires</strong> :
            </p>
            <ul className="mt-2 space-y-1 list-disc list-inside">
              <li><code className="font-mono text-xs bg-[var(--surface-3)] px-1">access_token</code> : session d&apos;authentification (httpOnly, Secure, SameSite=None, 12h)</li>
              <li><code className="font-mono text-xs bg-[var(--surface-3)] px-1">refresh_token</code> : prolongation de session (httpOnly, Secure, 30 jours)</li>
            </ul>
            <p className="mt-2">
              Aucun cookie publicitaire, de tracking ou d&apos;analytics tiers.
              Aucun cookie n&apos;est partagé avec des partenaires externes.
            </p>
          </Section>

          <Section icon={<Mail size={16} strokeWidth={2.4} />} title="Comment nous utilisons tes données">
            <ul className="space-y-1 list-disc list-inside">
              <li>Te permettre de te connecter et de gérer ton profil</li>
              <li>T&apos;envoyer des SMS (via Twilio) pour les demandes de disponibilités, les assignations et les réinitialisations de mot de passe</li>
              <li>Te lier à ton gestionnaire de tournoi pour la coordination des matchs</li>
              <li>Assurer la sécurité du service (détection de brute-force, audit log)</li>
            </ul>
            <p className="mt-2">
              Tes données ne sont <strong>jamais</strong> vendues à des tiers.
            </p>
          </Section>

          <Section icon={<UserX size={16} strokeWidth={2.4} />} title="Tes droits">
            <p>Conformément au RGPD et à la LPRPDE, tu peux à tout moment :</p>
            <ul className="mt-2 space-y-1 list-disc list-inside">
              <li>Consulter, corriger ou supprimer tes informations personnelles</li>
              <li>Demander une copie de toutes les données te concernant</li>
              <li>Retirer ton consentement (entraîne la suppression de ton compte)</li>
              <li>Déposer une plainte auprès de la CAI (Commission d&apos;accès à l&apos;information du Québec)</li>
            </ul>
            <p className="mt-2">
              Pour exercer ces droits, écris à{" "}
              <a href="mailto:info@lesamouraiduweb.com" className="underline">
                info@lesamouraiduweb.com
              </a>
              . Délai de réponse maximal : 30 jours.
            </p>
          </Section>

          <Section icon={<Shield size={16} strokeWidth={2.4} />} title="Sécurité">
            <ul className="space-y-1 list-disc list-inside">
              <li>Mots de passe hashés avec bcrypt (jamais stockés en clair)</li>
              <li>Connexions chiffrées TLS 1.2+ (HTTPS partout)</li>
              <li>Sessions automatiquement déconnectées après 30 minutes d&apos;inactivité</li>
              <li>Protection contre la force brute (5 tentatives → verrouillage 15 min)</li>
              <li>Sauvegardes quotidiennes chiffrées</li>
              <li>Audit log de toutes les actions sensibles</li>
            </ul>
          </Section>

          <Section icon={<Database size={16} strokeWidth={2.4} />} title="Durée de conservation">
            <ul className="space-y-1 list-disc list-inside">
              <li><strong>Compte actif</strong> : tant que tu utilises le service</li>
              <li><strong>Compte inactif</strong> : suppression automatique après 24 mois d&apos;inactivité (préavis par courriel 30 jours avant)</li>
              <li><strong>Journaux d&apos;audit</strong> : 12 mois</li>
              <li><strong>Demandes de réinitialisation de mot de passe</strong> : 15 minutes (puis effacées)</li>
            </ul>
          </Section>

          <div className="mt-8 pt-6 border-t border-[var(--border)] text-xs text-[var(--muted)]">
            <p>
              Cette politique peut évoluer. Toute modification importante te sera notifiée
              par courriel et affichée en haut de cette page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <section className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        <span
          className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "var(--surface-3)", border: "1px solid var(--border-strong)" }}
        >
          {icon}
        </span>
        <h2 className="font-serif text-xl font-bold leading-tight">{title}</h2>
      </div>
      <div className="text-sm text-[var(--text)] leading-relaxed pl-9">{children}</div>
    </section>
  );
}
